import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {Question} from '../../models/underwriter.model';
import {GlobalQuestionService} from '../../services/global-question.service';
import {QuestionFlowSessionQuery} from '../state/question-flow-session.query';
import {environment} from '../../../../../environments/environment';

declare const $: any;
declare function notify(): any;

// map styling from the design prototype (questionnaire-add-address.html)
const mapStyles: any[] = [
  {
    featureType: 'all',
    elementType: 'labels.text.stroke',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'administrative.neighborhood',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#333333',
      },
    ],
  },
  {
    featureType: 'poi.business',
    elementType: 'labels.text',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'poi.business',
    elementType: 'labels.icon',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'poi.place_of_worship',
    elementType: 'labels.text',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'poi.place_of_worship',
    elementType: 'labels.icon',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [
      {
        visibility: 'simplified',
      },
    ],
  },
  {
    featureType: 'road.local',
    elementType: 'labels.text',
    stylers: [
      {
        weight: 0.5,
      },
      {
        color: '#333333',
      },
    ],
  },
  {
    featureType: 'transit.station',
    elementType: 'labels.icon',
    stylers: [
      {
        gamma: 1,
      },
      {
        saturation: 50,
      },
    ],
  },
  {
    featureType: 'water',
    elementType: 'all',
    stylers: [
      {
        visibility: 'on',
      },
      {
        saturation: 50,
      },
      {
        hue: '#50a5d1',
      },
    ],
  },
];

@Component({
    selector: 'app-question-address-textbox',
    templateUrl: './question-address-textbox.component.html',
    styleUrls: ['./question-address-textbox.component.scss'],
    standalone: false
})
export class QuestionAddressTextboxComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {

  @ViewChild('addressText', {static: false}) addressText: ElementRef;
  @ViewChild('mapCanvas', {static: false}) mapCanvas: ElementRef;
  @ViewChild('modalUnitInput', {static: false}) modalUnitInput: ElementRef;
  @ViewChild('modalComplexInput', {static: false}) modalComplexInput: ElementRef;
  @Input() question: Question;
  @Input() readOnly = false;
  @Input() showLabel = true;
  @Input() labelText = '';
  @Input() address = '';
  @Input() showMap = true;
  @Input() pickLocation = true;
  @Output() questionAnswer = new EventEmitter<any>();
  @Output() pressNext = new EventEmitter<any>();

  map: google.maps.Map = null as any;
  marker: google.maps.Marker = null as any;
  autocomplete: google.maps.places.Autocomplete = null as any;
  autocompleteBoundEl: HTMLElement = null as any;

  apartmentNo = '';
  complex = '';
  streetAddress = '';
  suburb = '';
  province = '';
  city = '';
  postalCode = '';
  gps = '';

  placeIsValid = false;
  showError = false;
  // drives which inline form-control-error text shows - kept separate from showError so the
  // imprecise-address case (province/city only) can point the user at the fix instead of the
  // generic "invalid address" wording used for the no-components/wrong-country cases
  errorReason: 'invalid' | 'imprecise' = 'invalid';
  firstFocus = false;
  startedTyping = false;
  mapVisible = false;
  showAdditionalInfo = false;
  showAddDetailsLink = true;
  showUnit = false;
  showComplex = false;
  showPin = true;
  requirePreciseAddress = false;

  addresses: any = [];
  showAddresses = false;
  session_details: any;
  url = environment.cssURL;
  defaultCenter = {lat: -26.1714402, lng: 28.0050051}; // default to Johannesburg
  // restricts panning/zooming so the map (and therefore the draggable marker) can't leave South Africa
  mapRestriction: google.maps.MapRestriction = {
    latLngBounds: {north: -22, south: -35, west: 16, east: 33},
    strictBounds: true,
  };

  constructor(
    private ref: ChangeDetectorRef,
    private globalQuestionService: GlobalQuestionService,
    private questionFlowSessionQuery: QuestionFlowSessionQuery,
  ) {
  }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    this.getPlaceAutocomplete();

    // pickLocation mode: show map immediately with a draggable marker
    if (this.pickLocation) {
      this.initPickLocationMap();
    }

    // clear form fields in modal each time it opens
    if (typeof $ !== 'undefined') {
      $('#add-address-details-modal').on('show.bs.modal', () => {
        if (this.modalUnitInput) { this.modalUnitInput.nativeElement.value = ''; }
        if (this.modalComplexInput) { this.modalComplexInput.nativeElement.value = ''; }
        this.ref.detectChanges();
      });
    }

    if (this.question?.answer) {
      this.restoreAnswer(this.question.answer);
    } else if (this.address) {
      this.restoreAnswer(this.address);
    } else {
      this.globalQuestionService.spinner(true);
      this.session_details = this.questionFlowSessionQuery.getValue();
      if (!this.question?.cent_aq_question_id) { this.globalQuestionService.spinner(false); return; }
      const parameters = new Map<string, string>();
      parameters.set('session_id', this.session_details?.session_id);
      parameters.set('question_id', `${this.question.cent_aq_question_id}`);
      this.globalQuestionService.runCommandNew('getAddress', parameters).subscribe(addresses => {
        if (addresses.length > 0) {
          this.showAddresses = true;
          const add = [];
          let index = 0;
          addresses.forEach(a => {
            if (add.indexOf(a.cent_answer_data) === -1) {
              try {
                let json = JSON.parse(a.cent_answer_data);
                let address = '';
                if (json.apartmentNo && json.apartmentNo !== '') address += json.apartmentNo + ', ';
                if (json.complex && json.complex !== '') address += json.complex + ', ';
                address += json.streetAddress + ', ';
                address += json.suburb + ', ';
                address += json.province + ', ';
                address += json.city + ', ';
                address += json.postalCode;
                add.push({index: index, address: address, json: a.cent_answer_data});
                index++;
              }
              catch { }
            }
          });

          this.addresses = add;

        }
        this.globalQuestionService.spinner(false);
      });
    }

  }

  ngOnChanges(changes: SimpleChanges): void {
    this.applyQuestionParameters();
    this.getPlaceAutocomplete();

    // restoreAnswer() otherwise only runs once from ngAfterViewInit - callers that mount
    // this control before their data has loaded (e.g. edit-modal, which shows/hides via
    // CSS classes rather than *ngIf/@if recreation) never get the field populated once
    // [address]/[question] arrive later, since ngOnChanges doesn't retry it on its own
    if (this.addressText && (changes?.address || changes?.question)) {
      if (this.question?.answer) {
        this.restoreAnswer(this.question.answer);
      } else if (this.address) {
        this.restoreAnswer(this.address);
      }
    }
  }

  // question.parameter is a JSON string of {parameter, value} pairs (e.g. showMap/showComplex/showPin)
  // set per-question in the backend - only override a flag when its key is actually present.
  applyQuestionParameters(): void {
    if (!this.question?.parameter) { return; }
    let params: any[];
    try {
      params = JSON.parse(this.question.parameter);
    } catch {
      return;
    }
    const find = (name: string) => params?.find(p => p?.parameter === name)?.value;

    const showMapParam = find('showMap');
    const showComplexParam = find('showComplex');
    const showPinParam = find('showPin');
    const requirePreciseAddressParam = find('requirePreciseAddress');

    if (showMapParam !== undefined) { this.showMap = showMapParam === '1'; }
    if (showComplexParam !== undefined) { this.showAddDetailsLink = showComplexParam === '1'; }
    if (showPinParam !== undefined) { this.showPin = showPinParam === '1'; }
    if (requirePreciseAddressParam !== undefined) { this.requirePreciseAddress = requirePreciseAddressParam === '1'; }
  }

  ngOnDestroy(): void {
    if (typeof $ !== 'undefined') {
      $('#add-address-details-modal').off('show.bs.modal');
      $('#add-address-details-modal').modal('hide');
    }
  }

  // Prefill from a previously saved answer (JSON or legacy plain string) and
  // geocode it so the map, valid state and additional-info section restore.
  restoreAnswer(answer: string): void {
    let csvString = '';
    try {
      const address = JSON.parse(answer);
      this.apartmentNo = address?.apartmentNo || '';
      this.complex = address?.complex || '';
      this.streetAddress = address?.streetAddress || '';
      this.suburb = address?.suburb || '';
      this.province = address?.province || '';
      this.city = address?.city || '';
      this.postalCode = address?.postalCode || '';
      this.gps = address?.gps || '';
      csvString = [this.streetAddress, this.suburb, this.province, this.city, this.postalCode]
        .filter(v => v && v !== '').join(', ');
      if (this.apartmentNo) { this.showUnit = true; }
      if (this.complex) { this.showComplex = true; }
      if (this.apartmentNo || this.complex) { this.showAddDetailsLink = false; }
    }
    catch {
      csvString = answer;
    }
    if (!this.addressText || csvString.trim() === '') { return; }
    this.addressText.nativeElement.value = csvString;

    // stored lat/lng available: show the map directly without a geocode round-trip
    if (this.gps) {
      const parts = this.gps.split(',');
      const pos = {lat: parseFloat(parts[0]), lng: parseFloat(parts[1])};
      if (isFinite(pos.lat) && isFinite(pos.lng)) {
        if (this.showMap || this.pickLocation) {
          this.showMapAtLocation(pos);
        }
        this.showAdditionalInfo = true;
        this.placeIsValid = true;
        this.setValidMsg();
        this.ref.detectChanges();
        return;
      }
    }

    // get the latlng from the address string and show the map
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({address: csvString}, (results, status) => {
      if (status === google.maps.GeocoderStatus.OK) {
        const pos = {
          lat: results[0].geometry.location.lat(),
          lng: results[0].geometry.location.lng(),
        };
        this.gps = pos.lat + ', ' + pos.lng;
        if (this.showMap || this.pickLocation) {
          this.showMapAtLocation(pos);
        }
        this.showAdditionalInfo = true;
        this.placeIsValid = true;
        this.setValidMsg();
        this.ref.detectChanges();
      } else {
        console.log('Geocode was not successful for the following reason: ' + status);
      }
    });
  }

  getCountryCode(): string {
    return this.url.includes('india') ? 'IN' : 'ZA';
  }

  getCountryName(): string {
    return this.url.includes('india') ? 'India' : 'South Africa';
  }

  // the map's restriction bounds are a rectangle, so it can still show slivers of
  // neighbouring countries (e.g. Namibia/Botswana) at the edges - this catches those.
  // checks every result (not just the first) because the most specific reverse-geocode
  // result in rural areas is often a plus_code, which has no 'country' component at all
  isAllowedCountry(results: any[]): boolean {
    for (const result of results || []) {
      const country = result?.address_components?.find((c: any) => c.types?.indexOf('country') !== -1);
      if (country) { return country.short_name === this.getCountryCode(); }
    }
    return false;
  }

  // finding_id 55 follow-up: a province/city-only Google suggestion (e.g. "Eastern Cape,
  // South Africa") still returns address_components and passes isAllowedCountry, so it needs
  // its own precision gate - reject anything with no street-level (or premise/POI) component.
  // Gated on requirePreciseAddress so only risk-location questions enforce it; the map-pin
  // drag path (geocodePositionAndPopulate) is untouched, since it's the intended way in for
  // street-less rural/farm addresses.
  isPreciseAddress(components: any[]): boolean {
    const preciseTypes = [
      'street_number', 'route', 'premise', 'subpremise',
      'establishment', 'point_of_interest', 'plus_code',
      'natural_feature', 'airport', 'park'
    ];
    return (components || []).some((c: any) => c.types?.some((t: string) => preciseTypes.indexOf(t) !== -1));
  }

  getPlaceAutocomplete(): void {
    if (!this.addressText) { return; }
    // the input element is recreated when toggling the stored-address list,
    // so only rebind when it actually changed (avoids stacking instances)
    if (this.autocompleteBoundEl === this.addressText.nativeElement) { return; }
    this.autocompleteBoundEl = this.addressText.nativeElement;

    this.autocomplete = new google.maps.places.Autocomplete(this.addressText.nativeElement, {
      componentRestrictions: {country: this.getCountryCode()}
    });

    this.autocomplete.setFields([
      'address_components',
      'geometry',
      'icon',
      'name',
    ]);

    google.maps.event.addListener(this.autocomplete, 'place_changed', () => {
      this.onPlaceChanged();
    });
  }

  onPlaceChanged(): void {
    const place = this.autocomplete.getPlace();

    // Error: no address components
    if (!place?.address_components) {
      console.warn('No details available for input: \'' + place?.name + '\'');
      this.placeIsValid = false;
      this.errorReason = 'invalid';
      this.setValidMsg();
      if (!this.pickLocation) {
        this.mapVisible = false;
      }
      this.showAdditionalInfo = false;
      this.gps = '';
      this.ref.detectChanges();
      return;
    }

    // finding_id 55: the autocomplete dropdown's componentRestrictions only filters which
    // suggestions Google shows - it doesn't stop a selected/typed place from resolving outside
    // the allowed country, and this was the only address-entry path with no isAllowedCountry()
    // check at all (the map-drag/geolocate path already had one). Without it, q.answer got set
    // for an out-of-country address and the Next button - which only checks q.answer is truthy -
    // enabled with no country validation whatsoever, no devtools tampering required.
    if (!this.isAllowedCountry([place])) {
      notify()?.init({
        message: '<strong>Please choose a location within ' + this.getCountryName() + '.</strong>',
        icon: 'info',
        size: 'lg',
        style: 'error',
        speed: 350,
        duration: 4000
      });
      this.placeIsValid = false;
      this.errorReason = 'invalid';
      this.setValidMsg();
      this.showAdditionalInfo = false;
      this.gps = '';
      this.ref.detectChanges();
      return;
    }

    if (this.requirePreciseAddress && !this.isPreciseAddress(place.address_components)) {
      notify()?.init({
        message: '<strong>That address is too broad.</strong> Please include a street or building, ' +
          'or drag the map pin to the exact location.',
        icon: 'info',
        size: 'lg',
        style: 'error',
        speed: 350,
        duration: 4000
      });
      this.placeIsValid = false;
      this.errorReason = 'imprecise';
      this.setValidMsg();
      this.showAdditionalInfo = false;
      this.gps = '';
      this.ref.detectChanges();
      return;
    }

    // Success: place has valid address components
    this.placeIsValid = true;
    this.setValidMsg();

    const pos = {
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
    };

    if (this.showMap || this.pickLocation) {
      this.showMapAtLocation(pos);
    }
    this.showAdditionalInfo = true;

    // store GPS coords in hidden input
    this.gps = pos.lat + ', ' + pos.lng;

    // pickLocation mode: sync marker to the autocomplete-selected place
    if (this.pickLocation && this.marker) {
      this.marker.setPosition(pos);
      this.map.setZoom(15);
      this.map.setCenter(pos);      
    }

    this.populateAddress(place.address_components);
    this.ref.detectChanges();
  }

  // on first focus of address input, attempt to geolocate user & automatically populate address
  onInputFocus(e: any): void {
    const target = e.target as HTMLInputElement;
    target?.setAttribute('autocomplete', 'one-time-code');

    if (!this.firstFocus) {
      this.firstFocus = true;

      // don't geolocate over an address that's already valid (e.g. prepopulated/restored)
      if (this.placeIsValid) {
        return;
      }

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((position) => {

          // if accuracy is greater than 50m, don't use it
          if (position.coords.accuracy > 50) {
            console.log('Location accuracy too low, won\'t prepopulate address');
            return;
          }

          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          this.prefillFromPosition(pos);
        });
      }
    }
  }

  prefillFromPosition(pos: any): void {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({location: pos}, (results, status) => {
      if (status === google.maps.GeocoderStatus.OK) {

        // don't override if user already started typing, or a valid address is already set
        if (this.startedTyping || this.placeIsValid) {
          console.log('User started typing or a valid address is already set, won\'t overwrite');
          return;
        }

        // don't prepopulate from a geolocation that falls outside the allowed country
        if (!this.isAllowedCountry(results)) {
          console.log('Geolocated position is outside ' + this.getCountryCode() + ', won\'t prepopulate');
          return;
        }

        this.addressText.nativeElement.value = results[0].formatted_address;

        // open autocomplete dropdown based on populated address
        const event = new Event('input', {bubbles: true});
        this.addressText.nativeElement.dispatchEvent(event);

      } else {
        console.log('Error, cannot determine the address at this location');
      }
    });
  }

  // When the user leaves the input, if no valid place was selected
  // then show an error state on the input's form-group.
  onInputBlur(): void {
    this.setValidMsg();
  }

  // If user types manually, clear any previously stored GPS coords and hide map unless pickLocation keeps it visible
  onInputChanged(): void {
    this.startedTyping = true;
    this.placeIsValid = false;
    this.gps = '';
    if (!this.pickLocation) {
      this.mapVisible = false;
    }
    this.showAdditionalInfo = false;
  }

  setValidMsg(): void {
    this.showError = !this.placeIsValid;
  }

  initPickLocationMap(): void {
    this.mapVisible = true;
    this.ref.detectChanges();
    if (!this.mapCanvas) { return; }

    this.map = new google.maps.Map(this.mapCanvas.nativeElement, {
      center: this.defaultCenter,
      zoom: 5,
      restriction: this.mapRestriction,
      styles: mapStyles,
      mapTypeControl: false,
      streetViewControl: false
    });

    this.marker = new google.maps.Marker({
      map: this.showPin ? this.map : null,
      draggable: true,
      icon: this.markerImage(),
      position: this.defaultCenter,
    });

    // Try to geolocate user on init and move marker to their position.
    // If the user has already selected a valid address, don't override it.
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (this.placeIsValid) { return; }
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          this.map.setZoom(18);
          this.map.setCenter(pos);          
          this.marker.setPosition(pos);
          this.geocodePositionAndPopulate(pos);
        },
        (error) => {
          console.log('Geolocation failed or denied, marker left at default position');
        },
        {enableHighAccuracy: true, timeout: 10000}
      );
    }

    // On drag end, reverse geocode the new position and populate address fields.
    // Reset startedTyping so the input value is always updated when the user drags.
    google.maps.event.addListener(this.marker, 'dragend', () => {
      this.startedTyping = false;
      this.geocodePositionAndPopulate(this.marker.getPosition());
    });
  }

  // Reverse geocode a position and populate the address input + GPS coords
  geocodePositionAndPopulate(pos: any): void {
    const lat = typeof pos.lat === 'function' ? pos.lat() : pos.lat;
    const lng = typeof pos.lng === 'function' ? pos.lng() : pos.lng;
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({location: {lat, lng}}, (results, status) => {
      if (status === google.maps.GeocoderStatus.OK) {
        const place = results[0];

        // the restriction bounds are a rectangle, so a dropped/geolocated pin can still
        // land just outside the country (e.g. Namibia/Botswana slivers) - reject those
        if (!this.isAllowedCountry(results)) {
          notify()?.init({
            message: '<strong>Please choose a location within ' + this.getCountryName() + '.</strong>',
            icon: 'info',
            size: 'lg',
            style: 'error',
            speed: 350,
            duration: 4000
          });
          this.placeIsValid = false;
          this.setValidMsg();
          this.showAdditionalInfo = false;
          this.gps = '';
          this.ref.detectChanges();
          return;
        }

        if (!this.startedTyping) {
          this.addressText.nativeElement.value = place.formatted_address;
        }
        this.gps = lat + ', ' + lng;
        this.showAdditionalInfo = true;
        this.placeIsValid = true;
        this.setValidMsg();
        this.populateAddress(place.address_components);
        this.ref.detectChanges();
      } else {
        console.log('Error, cannot determine the address at this location');
      }
    });
  }

  showMapAtLocation(pos: any): void {
    // pickLocation mode: map already initialised, just pan and reposition marker
    if (this.pickLocation && this.map) {
      this.mapVisible = true;
      this.ref.detectChanges();
      // the map may have been created while its container was hidden (0x0), so it needs a resize to render
      google.maps.event.trigger(this.map, 'resize');
      this.map.setCenter(pos);
      this.map.setZoom(15);
      this.map.setCenter(pos);      
      if (this.marker) { this.marker.setPosition(pos); }
      return;
    }

    this.mapVisible = true;
    this.ref.detectChanges();
    if (!this.mapCanvas) { return; }

    this.map = new google.maps.Map(this.mapCanvas.nativeElement, {
      center: pos,
      zoom: 15,
      restriction: this.mapRestriction,
      styles: mapStyles,
      mapTypeControl: false,
      streetViewControl: false
    });

    this.marker = new google.maps.Marker({
      position: pos,
      map: this.showPin ? this.map : null,
      icon: this.markerImage(),
    });
  }

  markerImage(): any {
    return {
      url: 'assets/3/icn/marker.png',
      size: new google.maps.Size(77, 54),
      scaledSize: new google.maps.Size(77, 54),
      origin: new google.maps.Point(0, 0),
      anchor: new google.maps.Point(20, 50)
    };
  }

  // adding additional address details from modal to main form
  submitDetailsModal(e: any): void {
    e.preventDefault();
    const unitValue = (this.modalUnitInput?.nativeElement.value || '').trim();
    const complexValue = (this.modalComplexInput?.nativeElement.value || '').trim();

    if (unitValue) {
      this.apartmentNo = unitValue;
      this.showUnit = true;
    }
    if (complexValue) {
      this.complex = complexValue;
      this.showComplex = true;
    }

    this.showAddDetailsLink = false;
    if (typeof $ !== 'undefined') {
      $('#add-address-details-modal').modal('hide');
    }

    if (unitValue || complexValue) {
      this.emitAnswer();
    }
    this.ref.detectChanges();
  }

  onUnitChanged(e: any): void {
    this.apartmentNo = e.target.value;
  }

  onComplexChanged(e: any): void {
    this.complex = e.target.value;
  }

  // modal's unit/complex inputs aren't bound to a TS field (only read on submit), so the
  // char-count needs an explicit tick on every keystroke to stay in sync with the DOM value
  onModalFieldInput(): void {
    this.ref.detectChanges();
  }

  populateAddress(addressComponents: any): void {
    let addressStreetNumber;
    let addressStreetName;
    this.streetAddress = '';
    this.suburb = '';
    this.city = '';
    this.province = '';
    this.postalCode = '';

    for (const item of addressComponents) {
      // street incl. number
      if (item.types.indexOf('street_number') !== -1) {
        addressStreetNumber = item.short_name;
      }

      // Street Name
      if (item.types.indexOf('route') !== -1) {
        addressStreetName = item.long_name;
      }

      // Street Number
      if (addressStreetNumber) {
        // has both name and number
        if (addressStreetName) {
          this.streetAddress = `${addressStreetNumber} ${addressStreetName}`;
          // only number (even possible?)
        } else {
          this.streetAddress = addressStreetNumber;
        }
        // only name
      } else if (addressStreetName) {
        this.streetAddress = addressStreetName;
      }


      // suburb
      if ((item.types.indexOf('sublocality') !== -1
          || item.types.indexOf('sublocality_level_1') !== -1
          || item.types.indexOf('sublocality_level_2') !== -1)
        && item.types.indexOf('political') !== -1) {
        if (!this.suburb) {
          this.suburb = item.short_name;
        }
      }

      // city
      if (item.types.indexOf('locality') !== -1 && item.types.indexOf('political') !== -1) {
        this.city = item.short_name;
      }

      // province
      if (item.types.indexOf('administrative_area_level_1') !== -1 && item.types.indexOf('political') !== -1) {
        this.province = item.short_name;
      }

      // postal code
      if (item.types.indexOf('postal_code') !== -1) {
        this.postalCode = item.short_name;
      }
    }
    this.ref.detectChanges();

    this.emitAnswer();
  }

  emitAnswer(): void {
    const address = {
      apartmentNo: (this.apartmentNo || '').trim(),
      complex: (this.complex || '').trim(),
      streetAddress: this.streetAddress || '',
      suburb: this.suburb || '',
      province: this.province || '',
      city: this.city || '',
      postalCode: this.postalCode || '',
      gps: this.gps || ''
    };

    if (this.question) {
      this.question.answer = JSON.stringify(address);
      this.questionAnswer.emit(this.question);
    } else {
      this.questionAnswer.emit(JSON.stringify(address));
    }
  }

  setAddress(event): void {
    if (event === 'new') {
      this.showAddresses = false;
      this.ref.detectChanges();
      this.getPlaceAutocomplete();
      return;
    }
    this.question.answer = this.addresses[event.index]?.json;

    if (this.showAddresses) {
      this.questionAnswer.emit(this.question);
    }
  }

}
